import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEye, faEyeSlash, faCheckCircle, faStore } from "@fortawesome/free-solid-svg-icons";
import LoadingBar from "react-top-loading-bar";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../api";
import './Login.scss';

const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  let color = "#";
  for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xff).toString(16)}`).slice(-2);
  return color;
};

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const ref = useRef(null);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Shows "Compte concerné : x@y.com" before the user commits to a new password —
  // also doubles as an early invalid/expired-token check instead of only failing on submit.
  const [account, setAccount] = useState(null);
  const [checkingToken, setCheckingToken] = useState(!!token);
  const [tokenError, setTokenError] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/api/auth/reset-password/info`, { params: { token } })
      .then(({ data }) => setAccount(data))
      .catch(err => setTokenError(err.response?.data?.message || t("reset_password.error.invalid_token")))
      .finally(() => setCheckingToken(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t("reset_password.error.mismatch")); return; }
    if (password.length < 8)  { toast.error(t("reset_password.error.too_short")); return; }

    ref.current.continuousStart();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
      ref.current.complete();
    } catch (err) {
      toast.error(err.response?.data?.message || t("reset_password.error.invalid_token"));
      ref.current.complete();
    } finally {
      setLoading(false);
    }
  };

  if (!token || tokenError) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center login-wrapper">
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#ef4444", fontSize: 14 }}>{token ? tokenError : t("reset_password.invalid_link")}</p>
          <NavLink to="/forgot-password" className="btn btn-dark btn-sm rounded-pill mt-2">{t("reset_password.request_new_link")}</NavLink>
        </div>
      </div>
    );
  }

  if (checkingToken) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center login-wrapper">
        <span className="spinner-border text-secondary" />
      </div>
    );
  }

  return (
    <>
      <LoadingBar color="#2563eb" ref={ref} height={3} shadow />

      <div className="container-fluid min-vh-100 d-flex p-0 login-wrapper">
        <div className="d-flex flex-column justify-content-center align-items-center px-5 form-side">
          <div className="w-100" style={{ maxWidth: 450 }}>

            <div className="text-center mb-4">
              <NavLink to="/">
                <img src="/images/saydalani_logo.png" alt="Saydalani" style={{ height: 40 }} className="mb-4" />
              </NavLink>
              <h3 className="fw-bold mt-3 mb-2 welcome_back_title">{t("reset_password.title")}</h3>
              {!done && <p className="text-muted" style={{ fontSize: 14 }}>{t("reset_password.subtitle")}</p>}
            </div>

            {!done && account?.email && (
              <div style={{
                display: "flex", alignItems: "center", gap: 14,
                background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
                padding: "14px 16px", marginBottom: 24,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
                  background: stringToColor((account.firstName || "") + (account.lastName || "")),
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, fontWeight: 700,
                }}>
                  {`${(account.firstName || "").charAt(0)}${(account.lastName || "").charAt(0)}`.toUpperCase()}
                </div>
                <div style={{ minWidth: 0, textAlign: "start" }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 3px" }}>
                    {t("reset_password.for_account")}
                  </p>
                  <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account.firstName} {account.lastName}
                  </p>
                  <p style={{ fontSize: 12.5, color: "#6b7280", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account.email}
                  </p>
                  {account.pharmacyName && (
                    <p style={{ fontSize: 12, color: "#1d4ed8", margin: "4px 0 0", display: "flex", alignItems: "center", gap: 6 }}>
                      <FontAwesomeIcon icon={faStore} style={{ fontSize: 11 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account.pharmacyName}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {done ? (
              <div style={{
                background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
                padding: "24px", textAlign: "center", marginBottom: 24,
              }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 36, color: "#16a34a", marginBottom: 12 }} />
                <p style={{ fontSize: 14, color: "#166534", margin: "0 0 16px" }}>
                  {t("reset_password.success")}
                </p>
                <button className="btn btn-dark rounded-pill px-4" onClick={() => navigate("/login")}>
                  {t("reset_password.sign_in")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3" style={{ position: "relative" }}>
                  <input
                    type={showPwd ? "text" : "password"}
                    className="form-control"
                    placeholder={t("reset_password.new_password")}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    style={{ paddingRight: 42, height: 48, borderRadius: 10, fontSize: 14 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}
                  >
                    <FontAwesomeIcon icon={showPwd ? faEyeSlash : faEye} />
                  </button>
                </div>

                <div className="mb-4">
                  <input
                    type={showPwd ? "text" : "password"}
                    className="form-control"
                    placeholder={t("reset_password.confirm_password")}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    disabled={loading}
                    style={{ height: 48, borderRadius: 10, fontSize: 14 }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-dark btn-lg w-100 rounded-pill py-2"
                  style={{ fontSize: 16, fontWeight: 600 }}
                  disabled={loading}
                >
                  {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                  {t("reset_password.submit")}
                </button>
              </form>
            )}

            {!done && (
              <div className="text-center mt-4">
                <NavLink to="/login" style={{ fontSize: 13, color: "#6b7280", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} />
                  {t("reset_password.back_to_login")}
                </NavLink>
              </div>
            )}

            <div className="text-center login_footer small mt-4">
              <p className="small mb-0" style={{ color: "#ccc" }}>{t('landing.footer.copyright', { year: new Date().getFullYear() })}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
